import React, { useState } from 'react';

// USA States for dropdown
const US_STATES = [
  { code: 'AL', name: 'Alabama' }, { code: 'AK', name: 'Alaska' }, { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' }, { code: 'CA', name: 'California' }, { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' }, { code: 'DE', name: 'Delaware' }, { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' }, { code: 'HI', name: 'Hawaii' }, { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' }, { code: 'IN', name: 'Indiana' }, { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' }, { code: 'KY', name: 'Kentucky' }, { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' }, { code: 'MD', name: 'Maryland' }, { code: 'MA', name: 'Massachusetts' },
  { code: 'MI', name: 'Michigan' }, { code: 'MN', name: 'Minnesota' }, { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' }, { code: 'MT', name: 'Montana' }, { code: 'NE', name: 'Nebraska' },
  { code: 'NV', name: 'Nevada' }, { code: 'NH', name: 'New Hampshire' }, { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' }, { code: 'NY', name: 'New York' }, { code: 'NC', name: 'North Carolina' },
  { code: 'ND', name: 'North Dakota' }, { code: 'OH', name: 'Ohio' }, { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' }, { code: 'PA', name: 'Pennsylvania' }, { code: 'RI', name: 'Rhode Island' },
  { code: 'SC', name: 'South Carolina' }, { code: 'SD', name: 'South Dakota' }, { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' }, { code: 'UT', name: 'Utah' }, { code: 'VT', name: 'Vermont' },
  { code: 'VA', name: 'Virginia' }, { code: 'WA', name: 'Washington' }, { code: 'WV', name: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin' }, { code: 'WY', name: 'Wyoming' }
];

/**
 * LinkedIn-style left sidebar filter panel for approval workflow
 * Supports: Status, Date ranges, Location, Submitter, Provider
 */
const ApprovalFilterPanel = ({ 
  filters, 
  onFilterChange, 
  onClearAll,
  availableOptions = {}, // { states, cities, branches, submitters, providers }
  onClose // NEW: Callback to close the filter panel from parent
}) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    status: true,
    duplicates: false,
    time: false,
    location: false,
    submitter: false,
    leadership: false,
    misc: false
  });

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Count active filters (exclude default values that aren't real filters)
  const defaultValues = { status: 'all', duplicateStatus: 'all', searchMode: 'keyword', salesStatus: '', rvpName: '', rdName: '' };
  const activeFilterCount = Object.entries(filters).filter(([key, value]) => {
    if (key in defaultValues && value === defaultValues[key]) return false;
    if (!value) return false;
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === 'object') return value.from || value.to;
    return value !== '';
  }).length;

  // Quick date presets
  const applyDatePreset = (preset, field) => {
    const today = new Date();
    let from, to;

    switch (preset) {
      case 'last7':
        from = new Date(today);
        from.setDate(from.getDate() - 7);
        to = today;
        break;
      case 'last30':
        from = new Date(today);
        from.setDate(from.getDate() - 30);
        to = today;
        break;
      case 'next30':
        from = today;
        to = new Date(today);
        to.setDate(to.getDate() + 30);
        break;
      default:
        return;
    }

    onFilterChange(field, {
      from: from.toISOString().split('T')[0],
      to: to.toISOString().split('T')[0]
    });
  };

  // Multi-select handler
  const toggleMultiSelect = (field, value) => {
    const current = filters[field] || [];
    const newValue = current.includes(value)
      ? current.filter(v => v !== value)
      : [...current, value];
    onFilterChange(field, newValue);
  };

  return (
    <div className={`bg-[var(--pbfs-navy)]/60 backdrop-blur-sm border border-white/10 rounded-2xl transition-all duration-300 ${isCollapsed ? 'w-16' : 'w-80'} flex-shrink-0 overflow-hidden`}>
      
      {/* Header */}
      <div className="p-4 border-b border-white/10 flex items-center justify-between sticky top-0 bg-[var(--pbfs-navy)]/80 backdrop-blur-md z-10">
        {!isCollapsed && (
          <>
            <div>
              <h3 className="text-white font-semibold text-lg">Filters</h3>
              {activeFilterCount > 0 && (
                <p className="text-[var(--pbfs-green)] text-xs mt-1">
                  {activeFilterCount} active
                </p>
              )}
            </div>
            <button
              onClick={() => {
                if (onClose) {
                  onClose(); // Call parent's close function
                } else {
                  setIsCollapsed(true); // Fallback to internal state
                }
              }}
              className="text-white/40 hover:text-white transition-colors text-xl"
              title="Close filters"
            >
              ‹
            </button>
          </>
        )}
        {isCollapsed && (
          <button
            onClick={() => setIsCollapsed(false)}
            className="mx-auto text-white/40 hover:text-white transition-colors text-xl"
            title="Expand filters"
          >
            ›
          </button>
        )}
      </div>

      {/* Filter Content */}
      {!isCollapsed && (
        <div className="overflow-y-auto max-h-[calc(100vh-200px)] custom-scrollbar">
          <div className="p-4 space-y-4">

            {/* Clear All Button */}
            {activeFilterCount > 0 && (
              <button
                onClick={onClearAll}
                className="w-full py-2 px-4 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg text-white/60 hover:text-white text-sm transition-all"
              >
                Clear all filters
              </button>
            )}

            {/* A. STATUS FILTER */}
            <FilterSection
              title="Status"
              isExpanded={expandedSections.status}
              onToggle={() => toggleSection('status')}
            >
              <div className="space-y-2">
                {[
                  { value: 'pending', label: 'Pending' },
                  { value: 'approved', label: '✅ Approved' },
                  { value: 'rejected', label: '❌ Rejected' },
                  { value: 'all', label: '📋 All' }
                ].map(status => (
                  <button
                    key={status.value}
                    type="button"
                    onClick={() => onFilterChange('status', status.value)}
                    className="flex items-center gap-2 cursor-pointer group w-full text-left"
                  >
                    <span className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                      filters.status === status.value
                        ? 'border-[var(--pbfs-green)] bg-[var(--pbfs-green)]'
                        : 'border-white/30'
                    }`}>
                      {filters.status === status.value && (
                        <span className="w-1.5 h-1.5 rounded-full bg-white" />
                      )}
                    </span>
                    <span className="text-white/70 group-hover:text-white text-sm transition-colors">
                      {status.label}
                    </span>
                  </button>
                ))}
              </div>
            </FilterSection>

            {/* DUPLICATES FILTER */}
            <FilterSection
              title="Duplicates"
              isExpanded={expandedSections.duplicates}
              onToggle={() => toggleSection('duplicates')}
            >
              <div className="space-y-2">
                {[
                  { value: 'all', label: '📋 All' },
                  { value: 'none', label: '✅ No Duplicates' },
                  { value: 'canonical', label: '🎯 Canonical' },
                  { value: 'confirmed_duplicate', label: '🔄 Confirmed Duplicates' },
                  { value: 'suspected', label: '❓ Suspected Only' },
                  { value: 'not_duplicate', label: '❌ Marked Not Duplicate' }
                ].map(duplicate => (
                  <button
                    key={duplicate.value}
                    type="button"
                    onClick={() => onFilterChange('duplicateStatus', duplicate.value)}
                    className="flex items-center gap-2 cursor-pointer group w-full text-left"
                  >
                    <span className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                      filters.duplicateStatus === duplicate.value
                        ? 'border-[var(--pbfs-green)] bg-[var(--pbfs-green)]'
                        : 'border-white/30'
                    }`}>
                      {filters.duplicateStatus === duplicate.value && (
                        <span className="w-1.5 h-1.5 rounded-full bg-white" />
                      )}
                    </span>
                    <span className="text-white/70 group-hover:text-white text-sm transition-colors">
                      {duplicate.label}
                    </span>
                  </button>
                ))}
              </div>
            </FilterSection>

            {/* B. TIME-BASED FILTERS */}
            <FilterSection
              title="Date Ranges"
              isExpanded={expandedSections.time}
              onToggle={() => toggleSection('time')}
            >
              <div className="space-y-4">
                {/* Capture Date */}
                <div>
                  <label className="text-white/60 text-xs mb-2 block">Capture Date</label>
                  <div className="space-y-2">
                    <div className="flex gap-1">
                      {['last7', 'last30'].map(preset => (
                        <button
                          key={preset}
                          onClick={() => applyDatePreset(preset, 'captureDateFrom')}
                          className="flex-1 px-2 py-1 bg-white/5 hover:bg-white/10 rounded text-white/50 hover:text-white text-xs transition-all"
                        >
                          {preset === 'last7' ? 'Last 7d' : 'Last 30d'}
                        </button>
                      ))}
                    </div>
                    <input
                      type="date"
                      value={filters.captureDateFrom?.from || ''}
                      onChange={(e) => onFilterChange('captureDateFrom', { ...filters.captureDateFrom, from: e.target.value })}
                      className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-[var(--pbfs-green)]/50"
                      placeholder="From"
                    />
                    <input
                      type="date"
                      value={filters.captureDateFrom?.to || ''}
                      onChange={(e) => onFilterChange('captureDateFrom', { ...filters.captureDateFrom, to: e.target.value })}
                      className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-[var(--pbfs-green)]/50"
                      placeholder="To"
                    />
                  </div>
                </div>

                {/* Last Inspection */}
                <div>
                  <label className="text-white/60 text-xs mb-2 block">Last Inspection</label>
                  <div className="flex gap-2">
                    <input
                      type="date"
                      value={filters.lastInspectionFrom?.from || ''}
                      onChange={(e) => onFilterChange('lastInspectionFrom', { ...filters.lastInspectionFrom, from: e.target.value })}
                      className="flex-1 px-2 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-xs focus:outline-none focus:ring-2 focus:ring-[var(--pbfs-green)]/50"
                      placeholder="From"
                    />
                    <input
                      type="date"
                      value={filters.lastInspectionFrom?.to || ''}
                      onChange={(e) => onFilterChange('lastInspectionFrom', { ...filters.lastInspectionFrom, to: e.target.value })}
                      className="flex-1 px-2 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-xs focus:outline-none focus:ring-2 focus:ring-[var(--pbfs-green)]/50"
                      placeholder="To"
                    />
                  </div>
                </div>

                {/* Next Due */}
                <div>
                  <label className="text-white/60 text-xs mb-2 block">Next Due</label>
                  <div className="space-y-2">
                    <button
                      onClick={() => applyDatePreset('next30', 'nextDueFrom')}
                      className="w-full px-2 py-1 bg-white/5 hover:bg-white/10 rounded text-white/50 hover:text-white text-xs transition-all"
                    >
                      Due in next 30 days
                    </button>
                    <div className="flex gap-2">
                      <input
                        type="date"
                        value={filters.nextDueFrom?.from || ''}
                        onChange={(e) => onFilterChange('nextDueFrom', { ...filters.nextDueFrom, from: e.target.value })}
                        className="flex-1 px-2 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-xs focus:outline-none focus:ring-2 focus:ring-[var(--pbfs-green)]/50"
                        placeholder="From"
                      />
                      <input
                        type="date"
                        value={filters.nextDueFrom?.to || ''}
                        onChange={(e) => onFilterChange('nextDueFrom', { ...filters.nextDueFrom, to: e.target.value })}
                        className="flex-1 px-2 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-xs focus:outline-none focus:ring-2 focus:ring-[var(--pbfs-green)]/50"
                        placeholder="To"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </FilterSection>

            {/* C. LOCATION-BASED FILTERS */}
            <FilterSection
              title="Location"
              isExpanded={expandedSections.location}
              onToggle={() => toggleSection('location')}
            >
              <div className="space-y-3">
                {/* State */}
                <div>
                  <label className="text-white/60 text-xs mb-2 block">State</label>
                  <select
                    multiple
                    value={filters.states || []}
                    onChange={(e) => {
                      const selected = Array.from(e.target.selectedOptions, option => option.value);
                      onFilterChange('states', selected);
                    }}
                    className="w-full px-3 py-2 bg-[#00314F] border border-white/20 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-[var(--pbfs-green)]/50 max-h-40 overflow-y-auto"
                    style={{ colorScheme: 'dark' }}
                  >
                    {US_STATES.map(state => (
                      <option key={state.code} value={state.code} className="bg-[#00314F] text-white py-1">
                        {state.code} - {state.name}
                      </option>
                    ))}
                  </select>
                  {(filters.states || []).length > 0 ? (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {filters.states.map(code => (
                        <span key={code} className="inline-flex items-center gap-1 px-2 py-0.5 bg-[var(--pbfs-green)]/20 border border-[var(--pbfs-green)]/40 rounded-full text-xs text-[var(--pbfs-green)]">
                          {code}
                          <button
                            type="button"
                            onClick={() => onFilterChange('states', filters.states.filter(s => s !== code))}
                            className="hover:text-white transition-colors"
                          >&times;</button>
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p className="text-white/40 text-xs mt-1">Hold Ctrl/Cmd to select multiple</p>
                  )}
                </div>

                {/* City */}
                <div>
                  <label className="text-white/60 text-xs mb-2 block">City</label>
                  <select
                    value={filters.city || ''}
                    onChange={(e) => onFilterChange('city', e.target.value)}
                    className="w-full px-3 py-2 bg-[#00314F] border border-white/20 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-[var(--pbfs-green)]/50"
                    style={{ colorScheme: 'dark' }}
                  >
                    <option value="" className="bg-[#00314F] text-white">All Cities</option>
                    {(availableOptions.cities || []).map(city => (
                      <option key={city} value={city} className="bg-[#00314F] text-white">
                        {city}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Zip */}
                <div>
                  <label className="text-white/60 text-xs mb-2 block">Zip Code</label>
                  <input
                    type="text"
                    value={filters.zips || ''}
                    onChange={(e) => onFilterChange('zips', e.target.value)}
                    placeholder="77001 or 77001,77002"
                    className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-white/30 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--pbfs-green)]/50"
                  />
                  <p className="text-white/40 text-xs mt-1">Comma-separated for multiple</p>
                </div>

                {/* Nearest Branch */}
                <div>
                  <label className="text-white/60 text-xs mb-2 block">Nearest Branch</label>
                  <select
                    multiple
                    value={filters.nearestBranches || []}
                    onChange={(e) => {
                      const selected = Array.from(e.target.selectedOptions, option => option.value);
                      onFilterChange('nearestBranches', selected);
                    }}
                    className="w-full px-3 py-2 bg-[#00314F] border border-white/20 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-[var(--pbfs-green)]/50 max-h-32"
                    style={{ colorScheme: 'dark' }}
                  >
                    {(availableOptions.branches || ['Houston', 'Austin', 'Dallas', 'San Antonio']).map(branch => (
                      <option key={branch} value={branch} className="bg-[#00314F] text-white">
                        {branch}
                      </option>
                    ))}
                  </select>
                  {(filters.nearestBranches || []).length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {filters.nearestBranches.map(branch => (
                        <span key={branch} className="inline-flex items-center gap-1 px-2 py-0.5 bg-[var(--pbfs-green)]/20 border border-[var(--pbfs-green)]/40 rounded-full text-xs text-[var(--pbfs-green)]">
                          {branch}
                          <button
                            type="button"
                            onClick={() => onFilterChange('nearestBranches', filters.nearestBranches.filter(b => b !== branch))}
                            className="hover:text-white transition-colors"
                          >&times;</button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </FilterSection>

            {/* D. SUBMITTER / PROVIDER FILTERS */}
            <FilterSection
              title="Submitter & Provider"
              isExpanded={expandedSections.submitter}
              onToggle={() => toggleSection('submitter')}
            >
              <div className="space-y-3">
                {/* Submitted By Email */}
                <div>
                  <label className="text-white/60 text-xs mb-2 block">Submitted By (Email)</label>
                  <input
                    type="text"
                    value={filters.submitter || ''}
                    onChange={(e) => onFilterChange('submitter', e.target.value)}
                    placeholder="Enter submitter email..."
                    className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-white/30 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--pbfs-green)]/50"
                  />
                </div>

                {/* Current Provider */}
                <div>
                  <label className="text-white/60 text-xs mb-2 block">Current Provider</label>
                  <input
                    type="text"
                    value={filters.provider || ''}
                    onChange={(e) => onFilterChange('provider', e.target.value)}
                    placeholder="Enter provider name..."
                    className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-white/30 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--pbfs-green)]/50"
                  />
                </div>
              </div>
            </FilterSection>

            {/* E. SALES LEADERSHIP FILTERS */}
            <FilterSection
              title="Sales Leadership"
              isExpanded={expandedSections.leadership}
              onToggle={() => toggleSection('leadership')}
            >
              <div className="space-y-3">
                {/* RVP */}
                <div>
                  <label className="text-white/60 text-xs mb-2 block">Regional Vice President</label>
                  <select
                    value={filters.rvpName || ''}
                    onChange={(e) => onFilterChange('rvpName', e.target.value)}
                    className="w-full px-3 py-2 bg-[#00314F] border border-white/20 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-[var(--pbfs-green)]/50"
                    style={{ colorScheme: 'dark' }}
                  >
                    <option value="" className="bg-[#00314F] text-white">All RVPs</option>
                    {(availableOptions.rvps || []).map(rvp => (
                      <option key={rvp.id || rvp.name} value={rvp.name} className="bg-[#00314F] text-white">
                        {rvp.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* RD */}
                <div>
                  <label className="text-white/60 text-xs mb-2 block">Regional Director</label>
                  <select
                    value={filters.rdName || ''}
                    onChange={(e) => onFilterChange('rdName', e.target.value)}
                    className="w-full px-3 py-2 bg-[#00314F] border border-white/20 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-[var(--pbfs-green)]/50"
                    style={{ colorScheme: 'dark' }}
                  >
                    <option value="" className="bg-[#00314F] text-white">All RDs</option>
                    {(availableOptions.rds || []).map(rd => (
                      <option key={rd.id || rd.name} value={rd.name} className="bg-[#00314F] text-white">
                        {rd.name}{rd.reportsToName ? ` (${rd.reportsToName})` : ''}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </FilterSection>

            {/* F. MISC FILTERS */}
            <FilterSection
              title="Other Filters"
              isExpanded={expandedSections.misc}
              onToggle={() => toggleSection('misc')}
            >
              <div className="space-y-3">
                {/* Has Invalid Data */}
                <div>
                  <label className="flex items-center gap-2 cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={filters.hasInvalidData || false}
                      onChange={(e) => onFilterChange('hasInvalidData', e.target.checked)}
                      className="w-4 h-4 accent-[var(--pbfs-green)]"
                    />
                    <span className="text-white/70 group-hover:text-white text-sm transition-colors">
                      Has Invalid Data
                    </span>
                  </label>
                </div>
              </div>
            </FilterSection>

          </div>
        </div>
      )}

      {/* Custom Scrollbar Styles */}
      <style jsx>{`
        .custom-scrollbar {
          scrollbar-width: thin;
          scrollbar-color: rgba(13, 177, 75, 0.3) rgba(255, 255, 255, 0.05);
        }
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: rgba(255, 255, 255, 0.05);
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(13, 177, 75, 0.3);
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(13, 177, 75, 0.5);
        }
      `}</style>
    </div>
  );
};

/**
 * Collapsible filter section component
 */
const FilterSection = ({ title, isExpanded, onToggle, children }) => {
  return (
    <div className="border border-white/10 rounded-lg overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full px-4 py-3 bg-white/5 hover:bg-white/10 flex items-center justify-between transition-all"
      >
        <span className="text-white font-medium text-sm">{title}</span>
        <span className="text-white/60 text-lg transform transition-transform duration-200" style={{ transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }}>
          ▼
        </span>
      </button>
      {isExpanded && (
        <div className="p-4 bg-[var(--pbfs-navy)]/40">
          {children}
        </div>
      )}
    </div>
  );
};

export default ApprovalFilterPanel;

